/**
 * Box select for edit and object mode — two entry paths:
 *
 * 1. **Touch hold-then-drag**: hold a finger still for BOX_SELECT_HOLD_MS,
 *    then drag to draw a selection rectangle.
 * 2. **B key (Blender convention)**: press B to arm box select, then
 *    click-drag to draw the rectangle. Esc or click-without-drag exits.
 *
 * On release, all visible edit-mode elements or object-mode origins inside
 * the rectangle are selected. Shift adds to the existing selection.
 *
 * Suppressed when a command buffer is active or object/edit selection is not
 * the active workspace.
 *
 * Orbit suppression: sets `boxSelectActive` in the store, which
 * OrbitControlsGuard reads to disable camera controls during the drag.
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { composeSceneObjectWorldMatrix } from "../app/collectionGraph.ts";
import { useStore } from "../app/store.ts";
import { entityIdToStoreKey } from "../ecs/EntityId.ts";
import { getRegistry } from "../ecs/queryStore.ts";
import { vertexCentroid } from "../modeling/editSelection.ts";
import { topologyFaceCount, topologyFaceVerts } from "../modeling/topology.ts";
import { isWorldPointOccluded } from "./EditModeRenderer.tsx";
import {
  BOX_SELECT_HOLD_MS,
  TAP_DRAG_THRESHOLD_SQ,
} from "./interactionConstants.ts";

// ---------------------------------------------------------------------------
// Overlay DOM management
// ---------------------------------------------------------------------------

function createOverlay(container: HTMLElement): HTMLDivElement {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.border = "1px solid rgba(255, 255, 255, 0.7)";
  el.style.background = "rgba(255, 255, 255, 0.08)";
  el.style.pointerEvents = "none";
  el.style.zIndex = "10";
  el.style.display = "none";
  container.appendChild(el);
  return el;
}

function updateOverlayRect(
  el: HTMLDivElement,
  containerRect: DOMRect,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
) {
  el.style.left = `${Math.min(x0, x1) - containerRect.left}px`;
  el.style.top = `${Math.min(y0, y1) - containerRect.top}px`;
  el.style.width = `${Math.abs(x1 - x0)}px`;
  el.style.height = `${Math.abs(y1 - y0)}px`;
  el.style.display = "block";
}

// ---------------------------------------------------------------------------
// Projection helpers
// ---------------------------------------------------------------------------

const _v3 = new THREE.Vector3();
const _objMatrix = new THREE.Matrix4();

function projectWorldToScreen(
  wx: number,
  wy: number,
  wz: number,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number } | null {
  _v3.set(wx, wy, wz).project(camera);
  if (_v3.z < -1 || _v3.z > 1) return null;
  return {
    x: ((_v3.x + 1) / 2) * rect.width + rect.left,
    y: ((-_v3.y + 1) / 2) * rect.height + rect.top,
  };
}

function projectToScreen(
  lx: number,
  ly: number,
  lz: number,
  objMatrix: THREE.Matrix4,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number } | null {
  _v3.set(lx, ly, lz).applyMatrix4(objMatrix);
  return projectWorldToScreen(_v3.x, _v3.y, _v3.z, camera, rect);
}

function inRect(
  px: number,
  py: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  const lx = Math.min(x0, x1),
    rx = Math.max(x0, x1);
  const ly = Math.min(y0, y1),
    ry = Math.max(y0, y1);
  return px >= lx && px <= rx && py >= ly && py <= ry;
}

function canArmBoxSelect(state: {
  editMode: unknown;
  workspaceMode: string;
  commandBuffer: unknown;
}): boolean {
  return (
    !state.commandBuffer &&
    (state.editMode != null || state.workspaceMode === "object")
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BoxSelectHandler() {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;
    const parent = canvas.parentElement;
    if (!parent) return;
    const container: HTMLElement = parent;

    const overlay = createOverlay(container);

    // --- shared gesture state ---
    let holdTimer: ReturnType<typeof setTimeout> | null = null;
    let touchArmed = false; // touch hold fired, waiting for drag
    let kbArmed = false; // B key pressed, waiting for click-drag
    let active = false; // actively drawing the rectangle
    let pointerId = -1;
    let sx = 0,
      sy = 0;
    let cx = 0,
      cy = 0;

    function setStoreFlag(v: boolean) {
      useStore.setState({ boxSelectActive: v });
    }

    function reset() {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (touchArmed || kbArmed || active) {
        canvas.style.cursor = "";
        setStoreFlag(false);
      }
      touchArmed = false;
      kbArmed = false;
      active = false;
      pointerId = -1;
      overlay.style.display = "none";
    }

    // =================================================================
    // Keyboard entry: B to arm, Esc to cancel
    // =================================================================

    function onKeyDown(e: KeyboardEvent) {
      // Ignore when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Escape" && kbArmed) {
        e.preventDefault();
        reset();
        return;
      }

      if (
        e.key === "b" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !e.shiftKey
      ) {
        const s = useStore.getState();
        if (!canArmBoxSelect(s)) return;
        // Toggle: pressing B again while armed cancels
        if (kbArmed) {
          reset();
          return;
        }
        e.preventDefault();
        kbArmed = true;
        setStoreFlag(true);
        canvas.style.cursor = "crosshair";
      }
    }

    // =================================================================
    // Pointer handling (shared by touch-hold and keyboard paths)
    // =================================================================

    function onDown(e: PointerEvent) {
      // Second pointer → cancel (multi-touch gesture)
      if (pointerId !== -1 && e.pointerId !== pointerId) {
        reset();
        return;
      }
      if (e.button !== 0) return;

      const s = useStore.getState();
      if (!canArmBoxSelect(s)) return;

      // --- Keyboard-armed path: immediate click-drag (any pointer type) ---
      if (kbArmed) {
        pointerId = e.pointerId;
        sx = e.clientX;
        sy = e.clientY;
        cx = sx;
        cy = sy;
        active = false;
        // Don't start the hold timer — we're already armed
        return;
      }

      // --- Touch-hold path: only touch input ---
      if (e.pointerType !== "touch") return;

      pointerId = e.pointerId;
      sx = e.clientX;
      sy = e.clientY;
      cx = sx;
      cy = sy;
      touchArmed = false;
      active = false;

      holdTimer = setTimeout(() => {
        holdTimer = null;
        const s2 = useStore.getState();
        if (!canArmBoxSelect(s2)) return;
        touchArmed = true;
        setStoreFlag(true);
        canvas.style.cursor = "crosshair";
      }, BOX_SELECT_HOLD_MS);
    }

    function onMoveCapture(e: PointerEvent) {
      const isArmed = touchArmed || kbArmed;
      if (!holdTimer && !isArmed && !active) return;
      // No pointer down yet (e.g. mouse moving after B press before click)
      if (pointerId === -1) return;
      if (e.pointerId !== pointerId) return;

      cx = e.clientX;
      cy = e.clientY;
      const dx = cx - sx,
        dy = cy - sy;
      const d2 = dx * dx + dy * dy;

      // During touch hold countdown — cancel if moved too much
      if (holdTimer) {
        if (d2 > TAP_DRAG_THRESHOLD_SQ) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        return;
      }

      // Promote armed → active once beyond tap threshold
      if (isArmed && !active && d2 > TAP_DRAG_THRESHOLD_SQ) {
        active = true;
      }

      if (active) {
        updateOverlayRect(
          overlay,
          container.getBoundingClientRect(),
          sx,
          sy,
          cx,
          cy,
        );
      }
    }

    function onUpCapture(e: PointerEvent) {
      if (e.button !== 0) return;

      if (active) {
        performBoxSelect(sx, sy, cx, cy, e.shiftKey, camera, canvas);
        reset();
        return;
      }

      // Keyboard-armed but user clicked without dragging → exit box select
      if (kbArmed) {
        reset();
        return;
      }

      // Touch hold expired without drag → pass through as normal click
      reset();
    }

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMoveCapture, true);
    canvas.addEventListener("pointerup", onUpCapture, true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointercancel", reset);
    window.addEventListener("blur", reset);

    return () => {
      reset();
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMoveCapture, true);
      canvas.removeEventListener("pointerup", onUpCapture, true);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointercancel", reset);
      window.removeEventListener("blur", reset);
      overlay.remove();
    };
  }, [camera, gl]);

  return null;
}

// ---------------------------------------------------------------------------
// Selection logic
// ---------------------------------------------------------------------------

function performBoxSelect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  additive: boolean,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const state = useStore.getState();
  if (state.editMode) {
    performEditBoxSelect(x0, y0, x1, y1, additive, camera, canvas);
    return;
  }
  if (state.workspaceMode === "object") {
    performObjectBoxSelect(x0, y0, x1, y1, additive, camera, canvas);
  }
}

function performEditBoxSelect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  additive: boolean,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const state = useStore.getState();
  const em = state.editMode;
  if (!em) return;
  const obj = state.sceneObjects.get(em.objectId);
  if (!obj) return;

  const rect = canvas.getBoundingClientRect();
  const { vertices, topology } = obj;
  const { granularity, hiddenVertices, hiddenFaces } = em;
  const xray = em.xray ?? false;

  // World matrix composes collection.transform ∘ obj local so rectangle
  // projection lines up with the rendered overlay when the edit target is a
  // collection member. Falls back to identity on lookup miss — callers guard
  // against that above by bailing when obj/em are absent.
  composeSceneObjectWorldMatrix(
    state.sceneObjects,
    state.collections,
    em.objectId,
    _objMatrix,
  ) ?? _objMatrix.identity();

  const camOrigin = camera.position.clone();

  /** Project a local vertex to world and check occlusion in one step. */
  function visibleInRect(
    lx: number,
    ly: number,
    lz: number,
    adjacentVerts?: number[],
  ): boolean {
    const p = projectToScreen(lx, ly, lz, _objMatrix, camera, rect);
    if (!p || !inRect(p.x, p.y, x0, y0, x1, y1)) return false;
    if (xray) return true;
    const worldPos = _v3.set(lx, ly, lz).applyMatrix4(_objMatrix).clone();
    return !isWorldPointOccluded(worldPos, camOrigin, adjacentVerts);
  }

  const selVerts = new Set<number>();
  const selEdges = new Set<number>();
  const selFaces = new Set<number>();

  if (granularity === "vertex") {
    const vCount = vertices.length / 3;
    for (let i = 0; i < vCount; i++) {
      if (hiddenVertices.has(i)) continue;
      if (
        visibleInRect(
          vertices[i * 3],
          vertices[i * 3 + 1],
          vertices[i * 3 + 2],
          [i],
        )
      ) {
        selVerts.add(i);
      }
    }
  } else if (granularity === "edge") {
    const { edges } = topology;
    const edgeCount = edges.length / 2;
    for (let i = 0; i < edgeCount; i++) {
      const a = edges[i * 2],
        b = edges[i * 2 + 1];
      if (hiddenVertices.has(a) && hiddenVertices.has(b)) continue;
      const aVis = visibleInRect(
        vertices[a * 3],
        vertices[a * 3 + 1],
        vertices[a * 3 + 2],
        [a, b],
      );
      const bVis = visibleInRect(
        vertices[b * 3],
        vertices[b * 3 + 1],
        vertices[b * 3 + 2],
        [a, b],
      );
      if (aVis && bVis) {
        selEdges.add(i);
        selVerts.add(a);
        selVerts.add(b);
      }
    }
  } else if (granularity === "face") {
    const fCount = topologyFaceCount(topology);
    for (let fi = 0; fi < fCount; fi++) {
      if (hiddenFaces.has(fi)) continue;
      const fv = topologyFaceVerts(topology, fi);
      const c = vertexCentroid(vertices, fv);
      if (!c) continue;
      if (visibleInRect(c[0], c[1], c[2], fv)) {
        selFaces.add(fi);
      }
    }
  }

  if (selVerts.size > 0 || selEdges.size > 0 || selFaces.size > 0) {
    state.boxSelectEdit(selVerts, selEdges, selFaces, additive);
  } else if (!additive) {
    state.clearVertexSelection();
  }
}

function performObjectBoxSelect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  additive: boolean,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const rect = canvas.getBoundingClientRect();
  const registry = getRegistry();
  const hits = new Set<string>();

  for (const [, entity] of registry) {
    const transform = entity.components.transform;
    if (!transform) continue;
    const visibility = entity.components.visibility;
    if (visibility && !visibility.visible) continue;
    const projected = projectWorldToScreen(
      transform.position[0],
      transform.position[1],
      transform.position[2],
      camera,
      rect,
    );
    if (!projected || !inRect(projected.x, projected.y, x0, y0, x1, y1))
      continue;
    hits.add(entityIdToStoreKey(entity.id));
  }

  useStore.getState().boxSelectObjects(hits, additive);
}
